import React, { useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import '../../component-styles/NotificationSettings.css';

/* Mirrors DEFAULT_CLIENT_NOTIFICATION_PREFS in
   backend/src/utils/clientActivityNotifications.js — alerts are on unless the
   designer turns them off, so an account that never opens this page still
   hears about client activity. */
const DEFAULT_PREFS = {
  proposalViewed: true,
  invoiceViewed: true,
  purchaseOrderViewed: true,
  proposalApproved: true,
  changesRequested: true,
  paymentReceived: true,
};

const ALERTS = [
  {
    key: 'proposalViewed',
    label: 'A client opens a proposal',
    help: 'Sent the first time they open it, then at most once every 30 minutes.',
  },
  {
    key: 'invoiceViewed',
    label: 'A client opens an invoice',
    help: 'Useful before chasing a past-due invoice — you can see whether it was ever opened.',
  },
  {
    key: 'purchaseOrderViewed',
    label: 'A client opens a purchase order',
    help: 'Covers the order status page you share with clients. Vendors opening their copy are not tracked.',
  },
  {
    key: 'proposalApproved',
    label: 'A client approves a proposal',
  },
  {
    key: 'changesRequested',
    label: 'A client requests changes',
  },
  {
    key: 'paymentReceived',
    label: 'A client makes a payment',
    help: 'Covers invoices, proposal deposits, retainers and time billing invoices.',
  },
];

function parseRecipients(value) {
  const seen = new Set();
  return String(value || '')
    .split(/[,;\n]+/)
    .map((email) => email.trim())
    .filter((email) => {
      if (!email || seen.has(email.toLowerCase())) return false;
      seen.add(email.toLowerCase());
      return true;
    });
}

export default function NotificationSettings() {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [recipients, setRecipients] = useState('');
  const [accountEmail, setAccountEmail] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        const data = await res.json();
        const stored = data.clientNotifications || {};
        setPrefs({ ...DEFAULT_PREFS, ...stored });
        setRecipients(Array.isArray(stored.recipients) ? stored.recipients.join(', ') : '');
        setAccountEmail(data.accountEmail || data.company?.email || '');
      } catch (err) {
        console.error('Failed to load notification settings', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  const toggle = (key) => setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('idToken');
      const payload = ALERTS.reduce(
        (acc, alert) => ({ ...acc, [alert.key]: prefs[alert.key] !== false }),
        { recipients: parseRecipients(recipients) }
      );
      const res = await fetch(`${API_BASE}/api/settings/${userId}/client-notifications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ clientNotifications: payload }),
      });
      if (!res.ok) throw new Error('Save failed');

      // Reflect what was actually stored — the server drops anything that
      // isn't a valid address, so a typo shouldn't keep sitting in the field
      // looking like it saved.
      const saved = (await res.json().catch(() => null))?.clientNotifications;
      if (saved) {
        setPrefs({ ...DEFAULT_PREFS, ...saved });
        setRecipients(Array.isArray(saved.recipients) ? saved.recipients.join(', ') : '');
      }
      notify('Notification settings saved!');
    } catch (err) {
      console.error('Save notification settings failed', err);
      notify('Failed to save notification settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  const parsedRecipients = parseRecipients(recipients);

  return (
    <div className="notif-settings">
      <p className="notif-desc">
        Get an email the moment a client acts on something you sent, so you know where a proposal or
        invoice stands without having to ask. The same activity shows on each document&apos;s
        Activity feed, and the last time a client opened a document is shown in your Proposals and
        Invoices lists.
      </p>

      <h3 className="notif-heading">Email me when</h3>

      <div className="notif-list">
        {ALERTS.map((alert) => (
          <label key={alert.key} className="notif-row">
            <input
              type="checkbox"
              checked={prefs[alert.key] !== false}
              onChange={() => toggle(alert.key)}
            />
            <span className="notif-row-text">
              <span className="notif-row-label">{alert.label}</span>
              {alert.help && <span className="notif-row-help">{alert.help}</span>}
            </span>
          </label>
        ))}
      </div>

      <h3 className="notif-heading">Send alerts to</h3>

      <div className="form-row">
        <input
          type="text"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder={accountEmail || 'you@yourstudio.com'}
        />
        <p className="notif-hint">
          {parsedRecipients.length > 0
            ? `Alerts go to ${parsedRecipients.join(', ')}.`
            : `Leave blank to use your account email${accountEmail ? ` (${accountEmail})` : ''}.`}{' '}
          Separate multiple addresses with commas.
        </p>
      </div>

      <button className="save-btn" onClick={save} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}
